import { UzBadge } from '@utazzo/ui';
import React from 'react';

/**
 * Meal-type pill. Same size/shape as UzBadge (rounded-full, px-2 py-0.5,
 * text-xs) but colour-coded so operators can spot inclusive meal plans at
 * a glance across a room list:
 *
 *   BREAKFAST     sky      (light blue - matches the distance-from-landmark chip)
 *   HALF BOARD    amber    (breakfast + one meal)
 *   FULL BOARD    emerald  (all three meals)
 *   ALL INCLUSIVE violet   (meals + drinks + activities)
 *   ROOM ONLY /   default UzBadge (secondary grey)
 *   other
 *
 * TBO returns mealType in a few casings ("Breakfast", "HALF_BOARD", "Full
 * board") - the classifier lower-cases + matches on well-known substrings.
 */
export const MealBadge = ({
  mealType,
}: {
  mealType: string | null | undefined;
}): React.JSX.Element | null => {
  if (!mealType) return null;
  const display = mealType.replace(/_/g, ' ');
  const kind = classifyMeal(mealType);

  if (kind === 'other') {
    return <UzBadge tone="secondary">{display}</UzBadge>;
  }

  const palette = MEAL_PALETTE[kind];
  return (
    <span
      className={
        // Exact same shape tokens as UzBadge (rounded-full, px-2 py-0.5,
        // text-xs, font-medium, w-fit) so meal pills line up 1:1 with
        // Refundable/Non-refundable badges in the same row.
        'inline-flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ' +
        palette
      }
    >
      {display}
    </span>
  );
};

type MealKind = 'breakfast' | 'halfBoard' | 'fullBoard' | 'allInclusive' | 'other';

const MEAL_PALETTE: Record<Exclude<MealKind, 'other'>, string> = {
  breakfast: 'border-sky-200 bg-sky-50 text-sky-700',
  halfBoard: 'border-amber-200 bg-amber-50 text-amber-700',
  fullBoard: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  allInclusive: 'border-violet-200 bg-violet-50 text-violet-700',
};

function classifyMeal(raw: string): MealKind {
  const s = raw.toLowerCase();
  // Order matters: check specific "all inclusive" / "full" / "half" first so
  // "breakfast" isn't picked up when the string is "half board (with breakfast)".
  if (s.includes('all inclusive') || s.includes('allinclusive')) return 'allInclusive';
  if (s.includes('full board') || s.includes('fullboard')) return 'fullBoard';
  if (s.includes('half board') || s.includes('halfboard')) return 'halfBoard';
  if (s.includes('breakfast')) return 'breakfast';
  return 'other';
}
